// CLIENT.NETWORK_FAILURE_SILENT - the one global notice sink.
//
// A transport failure (a fetch that never received a reply) rejects a promise
// that no caller catches - runPending and useSubmitGuard rethrow by design, and
// the save handlers only inspect the RETURNED error, never a throw - so it lands
// in the global unhandledrejection listener (reportClientError). That listener
// logs it; this module is how it also tells the operator, without the listener
// needing a React handle.
//
// Fire-and-forget, framework-free, so reportClientError (a plain module that runs
// before hydration) can call showConnectionNotice() and a single mounted
// <ConnectionNotice> component, subscribed here, renders it. There is exactly one
// banner slot, so repeated failures never stack; a short coalesce window also
// stops a flood from resetting the banner's auto-dismiss forever.
//
// No claim is made about whether a save succeeded: `Failed to fetch` means the
// reply never arrived, not that the request never reached the server, so the
// copy tells the operator to refresh and check, never "saved" or "not saved".

type Listener = (message: string) => void;

const listeners = new Set<Listener>();

// Collapse repeated failures within this window into a single notice.
const COALESCE_MS = 4000;
// A notice fired just before the component mounts (an early-load failure) is
// still delivered on subscribe if it is this fresh, then dropped.
const PENDING_FRESH_MS = 8000;

// -Infinity, not 0, so the very first notice is never mistaken for one inside
// the coalesce window (0 would swallow any show in the first COALESCE_MS).
let lastShownAt = Number.NEGATIVE_INFINITY;
let pending: { message: string; at: number } | null = null;

/**
 * Show a connection notice. Returns true if this call will surface a banner,
 * false if it was coalesced into a recent one. `now` is injectable for tests.
 */
export function showConnectionNotice(
  message: string,
  now: number = Date.now(),
): boolean {
  if (now - lastShownAt < COALESCE_MS) return false;
  lastShownAt = now;
  if (listeners.size === 0) {
    // No banner mounted yet (a failure during the first paint): hold the most
    // recent message so the component can show it the moment it subscribes.
    pending = { message, at: now };
    return true;
  }
  for (const listener of listeners) listener(message);
  return true;
}

/** Subscribe the mounted banner. Flushes a fresh pending notice on subscribe. */
export function subscribeConnectionNotice(
  listener: Listener,
  now: number = Date.now(),
): () => void {
  listeners.add(listener);
  if (pending && now - pending.at < PENDING_FRESH_MS) {
    const { message } = pending;
    pending = null;
    listener(message);
  }
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: clear module state between cases. */
export function __resetConnectionNotice(): void {
  listeners.clear();
  lastShownAt = Number.NEGATIVE_INFINITY;
  pending = null;
}

// ---- MAPS.TEARDOWN_AND_OFFLINE B: the offline notice shares the one slot ----------
//
// While the browser is offline the slot shows OFFLINE_COPY and nothing else: it does
// not auto-dismiss, offers no Refresh (a refresh with no connection replaces the page
// with the browser's own offline screen) and claims nothing about saves. A lost reply
// pushed during the outage ("Lost connection to Trayvar. If you were saving something,
// refresh the page to check it went through.") is held, not stacked, and takes the slot
// when the connection is back - the first moment a refresh can actually check the save.
// With no lost reply, the slot simply clears on return.

export const OFFLINE_COPY = "You are offline. Trayvar will catch up when the connection is back.";

export type NoticeView = { kind: "none" } | { kind: "offline" } | { kind: "lost"; message: string };
export interface NoticeModel {
  view: NoticeView;
  /** A lost-reply notice that arrived (or was showing) while offline. */
  heldLost: string | null;
}
export type NoticeEvent =
  | { type: "offline" }
  | { type: "back" }
  | { type: "lost"; message: string }
  | { type: "dismiss" };

export const NO_NOTICE: NoticeModel = { view: { kind: "none" }, heldLost: null };

/** The one slot's state machine. Pure, for the smoke. */
export function reduceNotice(m: NoticeModel, e: NoticeEvent): NoticeModel {
  switch (e.type) {
    case "offline":
      return {
        view: { kind: "offline" },
        heldLost: m.view.kind === "lost" ? m.view.message : m.heldLost,
      };
    case "back":
      if (m.view.kind !== "offline") return m;
      return { view: m.heldLost ? { kind: "lost", message: m.heldLost } : { kind: "none" }, heldLost: null };
    case "lost":
      if (m.view.kind === "offline") return { ...m, heldLost: e.message };
      return { view: { kind: "lost", message: e.message }, heldLost: null };
    case "dismiss":
      return m.view.kind === "lost" ? { view: { kind: "none" }, heldLost: null } : m;
  }
}
